/**
 * Google Analytics 4 client bridge — catalog events, forms, scroll depth.
 *
 * @package DemocraticTools
 */

const SCROLL_MILESTONES = [25, 50, 75, 90];

/**
 * @param {Record<string, unknown>|undefined} config
 */
export function initAnalytics(config) {
  if (!config?.measurementId) {
    return;
  }

  const baseParams = () => ({
    dt_environment: String(config.environment ?? 'unknown'),
    dt_locale: String(config.locale ?? ''),
    dt_market: String(config.market ?? ''),
    page_type: String(config.pageType ?? 'other'),
    content_id: String(config.contentId ?? ''),
    page_path: String(config.path ?? '/'),
  });

  const hasServerConsent = () =>
    config.consentGranted === true
    || config.consentGranted === 1
    || config.consentGranted === '1';

  const hasCookieConsent = () => {
    const cookies = globalThis.document?.cookie ?? '';

    return /(?:^|;\s*)dt_cookie_consent=(all|analytics)(?:;|$)/.test(cookies);
  };

  const canTrack = () => {
    if (typeof globalThis.gtag !== 'function') {
      return false;
    }

    return hasServerConsent() || hasCookieConsent();
  };

  const ensureGtagConsent = () => {
    if (!hasServerConsent() && hasCookieConsent()) {
      globalThis.gtag('consent', 'update', { analytics_storage: 'granted' });
    }
  };

  /**
   * @param {string} eventName
   * @param {Record<string, unknown>} [params]
   */
  const track = (eventName, params = {}) => {
    if (!canTrack()) {
      return;
    }

    globalThis.gtag('event', eventName, {
      ...baseParams(),
      ...params,
    });
  };

  globalThis.dtAnalytics = {
    track,
    canTrack,
    config,
  };

  ensureGtagConsent();

  if (canTrack()) {
    track('page_view', {
      page_title: globalThis.document?.title ?? '',
      page_location: globalThis.location?.href ?? '',
    });
  }

  bindSubscribeForms(track);
  bindContributeForms(track);
  bindSearchForms(track);
  bindArticleReadDepth(track, config);
}

/**
 * @param {(eventName: string, params?: Record<string, unknown>) => void} track
 */
function bindSubscribeForms(track) {
  const forms = globalThis.document?.querySelectorAll('.dt-subscribe-form') ?? [];

  for (const form of forms) {
    form.addEventListener('focusin', () => {
      if (form.dataset.dtSubscribeStarted === '1') {
        return;
      }

      form.dataset.dtSubscribeStarted = '1';
      track('subscribe_start');
    });
  }

  globalThis.document?.addEventListener?.('dt:form:success', (event) => {
    const detail = event?.detail;

    if (!detail || typeof detail !== 'object') {
      return;
    }

    if (detail.form === 'subscribe') {
      track('subscribe_complete', { method: 'rest_submit' });
    }

    if (detail.form === 'contribute') {
      track('contribution_submit', { method: 'rest_submit' });
    }
  });
}

/**
 * @param {(eventName: string, params?: Record<string, unknown>) => void} track
 */
function bindContributeForms(track) {
  const forms = globalThis.document?.querySelectorAll('.dt-contribute-form') ?? [];

  for (const form of forms) {
    form.addEventListener('focusin', () => {
      if (form.dataset.dtContributeStarted === '1') {
        return;
      }

      form.dataset.dtContributeStarted = '1';
      track('contribution_start');
    });
  }
}

/**
 * @param {(eventName: string, params?: Record<string, unknown>) => void} track
 */
function bindSearchForms(track) {
  const forms = globalThis.document?.querySelectorAll('form.search-form, form[role="search"]') ?? [];

  for (const form of forms) {
    form.addEventListener('submit', () => {
      const input = form.querySelector('input[type="search"], input[name="s"]');
      const term = input && 'value' in input ? String(input.value).trim() : '';

      if (term === '') {
        return;
      }

      track('search_query', { search_term: term });
    });
  }
}

/**
 * @param {(eventName: string, params?: Record<string, unknown>) => void} track
 * @param {Record<string, unknown>} config
 */
function bindArticleReadDepth(track, config) {
  if (config.pageType !== 'article') {
    return;
  }

  const fired = new Set();
  const milestones = [...SCROLL_MILESTONES];

  const onScroll = () => {
    const doc = globalThis.document?.documentElement;
    const body = globalThis.document?.body;

    if (!doc || !body) {
      return;
    }

    const scrollHeight = Math.max(doc.scrollHeight, body.scrollHeight) - globalThis.innerHeight;

    if (scrollHeight <= 0) {
      return;
    }

    const depth = Math.round((globalThis.scrollY / scrollHeight) * 100);

    for (const milestone of milestones) {
      if (depth >= milestone && !fired.has(milestone)) {
        fired.add(milestone);
        track('article_read', { read_depth: milestone });
      }
    }
  };

  const scrollTarget = globalThis.window ?? globalThis;
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}
